/** Scalar and person-picker param types persisted in excursion/group templates (English identifiers). */
export type ParamValueType = "number" | "string" | "boolean" | "users" | "customers" | "guides";

export type ParamsTemplateEntry = {
  key: string;
  type: ParamValueType;
};

export const RESERVED_KEYS = ["__types__"];

export const VALID_TYPES: ParamValueType[] = [
  "number",
  "string",
  "boolean",
  "users",
  "customers",
  "guides",
];

/** Legacy DB/API values normalized to canonical English on read. */
const LEGACY_PERSON_ALIAS: Record<string, ParamValueType> = {
  utilisateurs: "users",
  clients: "customers",
  encadrants: "guides",
};

function coerceToParamValueType(normalized: string): ParamValueType | null {
  if ((VALID_TYPES as readonly string[]).includes(normalized)) {
    return normalized as ParamValueType;
  }

  const legacy = LEGACY_PERSON_ALIAS[normalized];

  return legacy ?? null;
}

export function normalizeParamType(value: unknown): ParamValueType {
  if (typeof value !== "string") return "string";

  const normalized = value.trim().toLowerCase();

  switch (normalized) {
    case "users":
    case "user":
    case "utilisateur":
    case "utilisateurs":
      return "users";
    case "customers":
    case "customer":
    case "client":
    case "clients":
      return "customers";
    case "guides":
    case "guide":
    case "encadrant":
    case "encadrants":
      return "guides";
    default:
      break;
  }

  const coerced = coerceToParamValueType(normalized);

  return coerced ?? "string";
}

/** True when the template declares a searchable person-picker (users list, CRM customers, or guides). */
export function isPersonPicklistParamType(type: unknown): boolean {
  if (typeof type !== "string") return false;

  const normalized = normalizeParamType(type).toLowerCase();

  return normalized === "users" || normalized === "customers" || normalized === "guides";
}

export function entriesFromTemplate(
  template: Record<string, unknown> | undefined,
): ParamsTemplateEntry[] {
  if (!template || typeof template !== "object") return [];
  return Object.entries(template)
    .filter(([k]) => !RESERVED_KEYS.includes(k))
    .map(([key, value]) => ({
      key,
      type: normalizeParamType(value),
    }));
}

export function templateFromEntries(entries: ParamsTemplateEntry[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const e of entries) {
    if (!e.key.trim()) continue;
    out[e.key.trim()] = e.type;
  }
  return out;
}
